import { useEffect, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, Building2, Users } from "lucide-react";
import {
  companyApi,
  type CreateCompanyData,
  type UpdateCompanyData,
} from "../services/companyApi";
import { CompanyForm } from "../components/CompanyForm";

import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCompanyUIStore } from "@/stores/companyStore";
import { Loader } from "@/components/Loader";
import { toast } from "sonner";
import { useCompaniesQuery } from "@/services/companyQueries";

export function CompaniesPage() {
  const queryClient = useQueryClient();
  const {
    searchTerm,
    isFormOpen,
    selectedCompany,
    formLoading,
    setSearchTerm,
    openCreateForm,
    openEditForm,
    closeForm,
    setFormLoading,
  } = useCompanyUIStore();

  const {
    data: companies = [],
    isLoading: loading,
    error: companiesError,
  } = useCompaniesQuery();

  const filteredCompanies = useMemo(() => {
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (company.address?.city || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  useEffect(() => {
    // Ensure companies are prefetched; the query hook already fetches on mount
  }, []);

  const createCompanyMutation = useMutation({
    mutationFn: (companyData: CreateCompanyData) =>
      companyApi.createCompany(companyData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      closeForm();
    },
    onSettled: () => setFormLoading(false),
  });
  const handleCreateCompany = async (companyData: CreateCompanyData) => {
    setFormLoading(true);
    await createCompanyMutation.mutateAsync(companyData).catch((err: unknown) =>
      toast.error(
        err instanceof Error ? err.message : "Failed to create company",
        {
          position: "bottom-right",
        }
      )
    );
  };

  const updateCompanyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyData }) =>
      companyApi.updateCompany(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      closeForm();
    },
    onSettled: () => setFormLoading(false),
  });
  const handleUpdateCompany = async (companyData: UpdateCompanyData) => {
    if (!selectedCompany) return;
    setFormLoading(true);
    await updateCompanyMutation
      .mutateAsync({ id: selectedCompany.id, data: companyData })
      .catch(() => undefined);
  };

  const deleteCompanyMutation = useMutation({
    mutationFn: (companyId: string) => companyApi.deleteCompany(companyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["companies"] }),
  });
  const handleDeleteCompany = async (companyId: string) => {
    await deleteCompanyMutation.mutateAsync(companyId).catch(() => undefined);
  };

  // Handlers now provided by zustand store: openCreateForm, openEditForm, closeForm

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Companies Management</h1>
        <Dialog
          open={isFormOpen}
          onOpenChange={(open) => {
            if (!open) closeForm();
          }}
        >
          <DialogTrigger asChild>
            <Button onClick={openCreateForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {selectedCompany ? "Edit Company" : "Create Company"}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-1">
              <CompanyForm
                company={selectedCompany || undefined}
                onSubmit={(data) => {
                  if (selectedCompany) {
                    handleUpdateCompany(data as UpdateCompanyData);
                  } else {
                    handleCreateCompany(data as CreateCompanyData);
                  }
                }}
                onCancel={closeForm}
                isLoading={formLoading}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {companiesError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {companiesError.message}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Companies ({filteredCompanies.length})</CardTitle>
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Orders</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-gray-500"
                  >
                    {searchTerm
                      ? "No companies found matching your search."
                      : "No companies found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow
                    key={company.id}
                    onMouseEnter={() => {
                      queryClient.prefetchQuery({
                        queryKey: ["company", company.id],
                        queryFn: () => companyApi.getCompany(company.id),
                        staleTime: 60 * 1000,
                      });
                    }}
                  >
                    <TableCell className="font-medium">
                      <Link to={`/admin/companies/${company.id}`} className="">
                        <div className="flex items-center space-x-2">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span>{company.name}</span>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      {company.address
                        ? `${company.address.city}, ${company.address.state}`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Link to={`/admin/users?companyId=${company.id}`}>
                        <div className="flex items-center space-x-1">
                          <Users className="h-3 w-3 text-gray-400" />
                          {company._count?.users || 0}
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>{company._count?.orders || 0}</TableCell>
                    <TableCell>{company._count?.products || 0}</TableCell>
                    <TableCell>{formatDate(company.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditForm(company)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete Company
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete {company.name}?
                                This action cannot be undone and may affect
                                associated users and orders.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteCompany(company.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
